import { HR, Tooltip } from 'flowbite-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Icon } from '#common/components/Icon';
import { voiceSettingsSchema, type VoiceSettingsDialogState } from '#common/models/voice-settings.ts';
import { Button } from './Button';
import { Dialog } from './Dialog';

export { voiceSettingsSchema };
export type { VoiceSettingsDialogState };

interface VoiceSettingsDialogProps {
    settings: VoiceSettingsDialogState;
    defaultSettings: VoiceSettingsDialogState;
    onChange: (settings: VoiceSettingsDialogState) => void;
    t: (key: string) => ReactNode;
    open: boolean;
    onClose: () => void;
}

export function VoiceSettingsDialog({
    settings,
    defaultSettings,
    onChange,
    t,
    open,
    onClose,
}: VoiceSettingsDialogProps) {
    const [ localSettings, setLocalSettings ] = useState<VoiceSettingsDialogState>(settings);

    // Sync local settings when dialog opens or settings change
    useEffect(() => {
        if (open) {
            setLocalSettings(settings);
        }
    }, [ open, settings ]);

    const handleSliderChange = (key: keyof VoiceSettingsDialogState, value: number) => {
        const newSettings = { ...localSettings, [key]: value };

        const validation = voiceSettingsSchema.safeParse(newSettings);
        if (validation.success) {
            setLocalSettings(newSettings);
        } else {
            console.warn('Invalid voice setting value:', validation.error);

            const clampedValue = Math.max(
                Math.min(value, key === 'speed' ? 1.20 : 1),
                key === 'speed' ? 0.70 : 0
            );
            setLocalSettings({ ...localSettings, [key]: clampedValue });
        }
    };

    const resetToDefaults = () => {
        setLocalSettings(defaultSettings);
    };

    const applySettings = () => {
        onChange(localSettings);
        onClose();
    };

    const showStabilityWarning = localSettings.stability < 0.3;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            icon='heroicons--adjustments-vertical'
            title={t('voice-settings-title')}
            description={t('voice-settings-message')}
            showCloseIcon
        >
            <div className='mt-6 origin-top bg-white/10 backdrop-blur-sm rounded-xl p-6 space-y-6'>
                {/* Speed */}
                <div className='space-y-2'>
                    <div className='flex items-center gap-2'>
                        <label className='block  font-medium'>{t('speed')}: {localSettings.speed.toFixed(2)}</label>
                        <Tooltip content={<div className='max-w-64 text-sm'>{t('speed-tooltip')}</div>}>
                            <Icon icon='heroicons--information-circle' className=' text-sm cursor-help' />
                        </Tooltip>
                    </div>
                    <input
                        type='range'
                        min='0.70'
                        max='1.20'
                        step='0.01'
                        value={localSettings.speed}
                        onChange={(e) => handleSliderChange('speed', parseFloat(e.target.value))}
                        className='w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider'
                    />
                    <div className='flex justify-between text-sm '>
                        <span>{t('slower')} (0.70)</span>
                        <span>{t('faster')} (1.20)</span>
                    </div>
                </div>

                {/* Stability */}
                <div className='space-y-2'>
                    <div className='flex items-center gap-2'>
                        <label className='block font-medium'>
                            {t('stability')}: {Math.round(localSettings.stability * 100)}%
                        </label>
                        <Tooltip content={<div className='max-w-64 text-sm'>{t('stability-tooltip')}</div>}>
                            <Icon icon='heroicons--information-circle' className='text-sm cursor-help' />
                        </Tooltip>
                    </div>
                    <input
                        type='range'
                        min='0'
                        max='1'
                        step='0.01'
                        value={localSettings.stability}
                        onChange={(e) => handleSliderChange('stability', parseFloat(e.target.value))}
                        className='w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider'
                    />
                    <div className='flex justify-between text-sm'>
                        <span>{t('more-variable')} (0%)</span>
                        <span>{t('more-stable')} (100%)</span>
                    </div>
                    {showStabilityWarning && (
                        <div className='flex items-center gap-2 text-yellow-300 text-sm'>
                            <Icon icon='heroicons--exclamation-triangle' className='text-sm' />
                            <span>{t('stability-warning')}</span>
                        </div>
                    )}
                </div>

                {/* Similarity Boost */}
                <div className='space-y-2'>
                    <div className='flex items-center gap-2'>
                        <label className='block font-medium'>
                            {t('similarity')}: {Math.round(localSettings.similarityBoost * 100)}%
                        </label>
                        <Tooltip content={<div className='max-w-64 text-sm z-30'>{t('similarity-tooltip')}</div>}>
                            <Icon icon='heroicons--information-circle' className='text-sm cursor-help' />
                        </Tooltip>
                    </div>
                    <input
                        type='range'
                        min='0'
                        max='1'
                        step='0.01'
                        value={localSettings.similarityBoost}
                        onChange={(e) => handleSliderChange('similarityBoost', parseFloat(e.target.value))}
                        className='w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider'
                    />
                    <div className='flex justify-between text-sm'>
                        <span>{t('low')} (0%)</span>
                        <span>{t('high')} (100%)</span>
                    </div>
                </div>
            </div>
            <HR className='my-6' />
            <div className='grid grid-cols-2 gap-3'>
                <Button
                    color='white'
                    size='normal'
                    onClick={resetToDefaults}
                    type='button'
                    className='w-full border border-gray-300 hover:bg-gray-50'
                >
                    {t('reset-values')}
                </Button>
                <Button color='blue' size='normal' onClick={applySettings} type='button' className='w-full'>
                    {t('apply-settings')}
                </Button>
            </div>
        </Dialog>
    );
}
